<script setup lang="ts">
import { reactive, ref, computed, h, watch, onUnmounted } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import cloneDeep from 'lodash/cloneDeep';
import { PACKAGE, STATUS_CLOSED, STATUS_OPEN, LANG_FRENCH, INCENTIVE_GROUP_TYPE } from '@/constants';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useIndexStore } from '@/store/IndexStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useGatewayStore } from '@/store/GatewayStore';
import { useDestinationStore } from '@/store/DestinationStore';
import { useDurationStore } from '@/store/DurationStore';
import { useHotelStore } from '@/store/HotelStore';
import { useSubmitQuoteStore } from '@/store/SubmitQuoteStore';
import { useVuelidate } from '@vuelidate/core';
import { useTabComposable } from './useTabComposable';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import PDFModal from '@/components/modal/PDFModal.vue';
import SubmitNotificationModal from '@/components/modal/SubmitNotificationModal.vue';
import ValidateQuoteFormNotification from '@/components/modal/ValidateQuoteFormNotification.vue';
import WaitingSubmitModal from './WaitingSubmitModal.vue';
import ValidateOptionModal from './ValidateOptionModal.vue';
import PackageFormOption from '@/components/tabs/PackageQuoteForm/PackageFormOption.vue';

interface Props {
  advancedForm: any;
  isValidation: boolean;
  avgPaxRoomData: number;
  selectedLanguage: string;
  quoteIntro: object;
  calendarData?: object;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  submit: [];
  clear: [];
}>();

const {
  getUserParam,
  getUserQuote,
  formatAirport,
  initPackageData,
  ruleValue,
  checkTotalAdultAndRoom,
  checkAdultEachPackages,
  checkRoomsEachPackage,
  renderPackageTemp,
  validateDateTime,
  renderOriginQuote,
  checkTotalPassenger,
  checkTotalNumberOfRooms,
  cleanEmptyRooms,
  validateBlackoutDate
} = useTabComposable();

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const dialog = useDialog();

const currentLanguage = getCurrentLocale();
const adminSetupStore = useAdminSetupStore();
const gatewayStore = useGatewayStore();
const destinationStore = useDestinationStore();
const durationStore = useDurationStore();
const viewQuoteStore = useViewQuoteStore();
const hotelStore = useHotelStore();
const indexStore = useIndexStore();
const submitQuoteStore = useSubmitQuoteStore();

const isShowError = ref(false);
const isClearAgencyInfo = ref(false);
const getRoomsLoadings = ref([[]]);
const dateValue = ref([]);
const durationDate = ref([]);

const today = new Date();
const currentDay = today.getDate();
today.setDate(currentDay + 346);

const packageData = reactive<any>([initPackageData()]);
const packageInstaStatus = ref([[STATUS_OPEN]]);
const quote = computed(() => viewQuoteStore.selectQuote);
const isLoading = computed(() => submitQuoteStore.selectIsLoading);
const destinationCode = computed(() => destinationStore.selectDestinationByCode);
const dataSubmit = computed(() => submitQuoteStore.selectDataSubmit);
const isWorksheetRequote = computed(() => viewQuoteStore.isWorksheetRequote);
const isRequote = computed(() => viewQuoteStore.selectIsRequote);
const isSocketConnected = computed(() => indexStore.isSocketConnected);
const packageMinimumConfig = computed(() => adminSetupStore.minimumConfig?.packageConfig);

const gatewaysByDestination = ref([]);

const rules = computed(() => ruleValue);

const v$ = useVuelidate(rules, packageData);

if (quote.value.packages) {
  if (isRequote.value) {
    packageInstaStatus.value = [];
    quote.value.packages.forEach(async (p, index) => {
      packageInstaStatus.value.push([]);
      if (index > 0) {
        packageData.push(initPackageData());
      }
      getRoomsLoadings.value.push([]);
      p.hotels.forEach((h, indexH) => {
        if (indexH > 0) {
          packageData[index]?.hotels.push({
            hotel: '',
            rooms: [
              {
                room: ''
              }
            ]
          });
        }

        h?.rooms?.forEach((_: any, indexRoom: number) => {
          if (indexRoom > 0) {
            packageData[index]?.hotels[indexH]?.rooms.push({
              room: ''
            });
          }
        });
      });
      const selectedGateway = gatewayStore.getGatewaySelectedQuote(p.gateCode);
      packageData[index].gateway = {
        ...selectedGateway,
        airportNameLabel: selectedGateway.airportName,
        airportNameLabelFr: selectedGateway.airportNameFR
      };
      const listDestinationsId = await gatewayStore.getDestinationsByGateCode(packageData[index].gateway?.code);
      const payload = {
        listDestinationsId,
        index
      };
      await destinationStore.getDestinationsMultiple(payload, currentLanguage);
      packageData[index].adults = p.adtNo;
      packageData[index].children = p.chdNo;
      packageData[index].numOfRooms = p.numOfRooms;
      packageData[index].brochure = p.brochure;
      destinationStore.getDestinationByCode(p.destCode);
      packageData[index].destination = destinationCode.value;
      packageData[index].additionalNote = p.additionalNote;
      if (destinationCode.value) {
        const gatewayDuration = await gatewayStore.getGateWayDuration(p?.gateCode, p?.destCode);

        const durations = gatewayStore.getDurationByGatewayAndDestination(p.gateCode, p.destCode, gatewayDuration).map((item) => {
          return { name: item };
        });
        durationStore.updateDurationsMultiple({ durations, index });
        packageData[index].duration = { name: p.duration };

        await gatewayStore.getGateWaysByDestination(destinationCode.value.code).then((res) => {
          gatewaysByDestination.value = formatAirport(res, currentLanguage);
        });
      }
    });
  }
}

const addOptionPackage = () => {
  packageData.push({
    gateway: '',
    destination: packageData[0].destination,
    dateRange: undefined,
    duration: {},
    adults: 0,
    children: 0,
    numOfRooms: 0,
    hotels: packageData[0].hotels,
    additionalNote: '',
    brochure: packageData[0].brochure
  });
  getRoomsLoadings.value.push([]);
  v$.value = useVuelidate(rules, packageData);
  packageInstaStatus.value.push([STATUS_CLOSED]);
  if (packageData[0].gateway.code && packageData[0].destination.code) {
    gatewayStore.getGateWaysByDestination(packageData[0].destination.code).then((res) => {
      gatewaysByDestination.value = formatAirport(res, currentLanguage);
    });
    gatewayStore.getGateWayDuration(packageData[0].gateway.code, packageData[0].destination.code, packageData[0].dateRange).then((res) => {
      const durationList = gatewayStore.getDurationByGatewayAndDestination(packageData[0].gateway.code, packageData[0].destination.code, res);
      if (durationList.length) {
        const durations = durationList.map((item) => {
          return { name: item };
        });
        packageData.forEach((packageItem, indexPackage) => {
          if (indexPackage > 0) {
            durationStore.updateDurationsMultiple({ durations, index: indexPackage });
            updateDurationDate({ value: packageItem.duration }, indexPackage, packageData);
          }
        });
      }
    });
  }
};

const removeOptionPackage = () => {
  packageData.pop();
  getRoomsLoadings.value.pop();
  v$.value = useVuelidate(rules, packageData);
  packageInstaStatus.value.pop();
};

const applyHotel = (packageList) => {
  packageList.forEach((packageItem, packageIndex) => {
    if (packageIndex !== 0) {
      packageItem.hotels = packageData[0].hotels;
    }
  });
};

const addHotelPackage = (index: number) => {
  packageInstaStatus.value[index]?.push(STATUS_CLOSED);

  packageData[index]?.hotels?.push({
    hotel: '',
    rooms: [{ room: '' }]
  });
  applyHotel(packageData);

  getRoomsLoadings.value[index]?.push(false);
  v$.value = useVuelidate(rules, packageData);
};

const removeHotelPackage = (index: number) => {
  packageData[index]?.hotels?.pop();
  applyHotel(packageData);
  packageInstaStatus.value[index]?.pop();
  getRoomsLoadings.value[index]?.pop();
};

const addRoomPackage = (index: number, indexHotel: number) => {
  packageData[index].hotels[indexHotel].rooms.push({
    room: ''
  });
  applyHotel(packageData);
  v$.value = useVuelidate(rules, packageData);
};

const removeRoomPackage = (index: number, indexHotel: number) => {
  packageData[index].hotels[indexHotel].rooms.pop();
  applyHotel(packageData);
  v$.value = useVuelidate(rules, packageData);
};

const clearDataPackage = () => {
  removePackage();
  durationDate.value = [];
  dateValue.value = [];
  isShowError.value = false;
  hotelStore.clearRoomCategory();
  viewQuoteStore.updateViewQuote({});
};

const clearAllDataPackage = () => {
  clearDataPackage();
  viewQuoteStore.getAction('view');
  viewQuoteStore.getActionRequoteFromWorksheet(true);
  emits('clear');
  isClearAgencyInfo.value = true;
};

const onAfterSubmitQuote = (quoteData: any) => {
  clearDataPackage();
  handleShowConfirmModalPackage(quoteData.pdfUrl, quoteData.quoteId);
};

const resetPackageData = (packageItem: any) => {
  packageItem.gateway = '';
  packageItem.destination = '';
  packageItem.dateRange = undefined;
  packageItem.duration = '';
  packageItem.adults = 0;
  packageItem.children = 0;
  packageItem.numOfRooms = 0;
  packageItem.additionalNote = '';
};

const showModalErrorPackage = () => {
  const modalRef = dialog.open(ValidateQuoteFormNotification, {
    data: {
      message: t('notification.over_days')
    },
    props: {
      header: t('common.labels.unable_to_process'),
      modal: true
    },
    templates: {
      footer: () => {
        return [h('div', { class: 'text-center' }, h(Button, { autofocus: true, label: t('common.buttons.close'), onClick: () => modalRef.close() }))];
      }
    }
  });
};

const showValidateOptionModal = () => {
  const dialogValidationRef = dialog.open(ValidateOptionModal, {
    props: {
      header: t('create_quote.confirm'),
      modal: true,
      style: {
        width: '28vw'
      }
    },
    emits: {
      isSave: (event: any) => {
        handleSubmitOption(event);
        dialogValidationRef.close();
      }
    }
  });
};

const showConfirmQuoteCriteria = (packages: any[], passengers?: number, rooms?: number, isMultipleOptionForSamePassenger?: boolean) => {
  const dialogRef = dialog.open(ValidateQuoteFormNotification, {
    data: {
      message: `${t('advance_quote.confirm_criteria')} ${passengers} ${t('advance_quote.passengers')} and ${rooms} ${t('common.rooms')}`
    },
    templates: {
      footer: () => {
        return [
          h(
            'div',
            { class: 'text-center' },
            h(Button, {
              label: t('advance_quote.form.submit'),
              autofocus: true,
              onClick: () => {
                checkPayloadSubmitQuote(packages, isMultipleOptionForSamePassenger);
                dialogRef.close();
              }
            })
          )
        ];
      }
    },
    props: {
      header: t('advance_quote.form.notice'),
      modal: true
    }
  });
};

const checkMaxRooms = async (quoteData: any) => {
  let isValid: boolean = true;
  if (quoteData.gType !== INCENTIVE_GROUP_TYPE) {
    const payload: any[] = [];
    quoteData.packages.forEach((item: any) => {
      const hotelData: any = [];
      item.hotels.forEach((hotel: any) => {
        let hotelCodes: string = '';
        const roomCodes: any[] = [];
        if (hotel.hCode) {
          hotelCodes = hotel.hCode;
        }
        hotel.rooms.forEach((room: any) => {
          if (room.code) {
            roomCodes.push(room.code);
          }
        });
        hotelData.push({
          hotelCodes: hotelCodes,
          roomCodes: roomCodes
        });
      });
      payload.push({
        brochureCode: item.brochure,
        destinationCode: item.destCode,
        hotelData: hotelData,
        numberOfRooms: item.numOfRooms
      });
    });
    isValid = await adminSetupStore.checkMaxRooms(payload);
  }
  return isValid;
};

const checkHotelInBlackoutDate = (packagesData: any[]) => {
  return packagesData.some((packageData: any) => {
    return !validateBlackoutDate(packageData.hotels);
  });
};

const showValidateQuoteFormNotification = (message: string = t('advance_quote.form.min_pax_package', { minPax: packageMinimumConfig.value.minPax, minRoom: packageMinimumConfig.value.minRoomsCabins })) => {
  const dialogRef = dialog.open(ValidateQuoteFormNotification, {
    props: {
      header: t('advance_quote.form.notice'),
      modal: true,
      style: {
        maxWidth: '90vw'
      }
    },
    data: {
      message: message
    },
    templates: {
      footer: () => {
        return [
          h(
            'div',
            { class: 'text-center' },
            h(Button, {
              label: t('advance_quote.form.return_to_form'),
              autofocus: true,
              onClick: () => dialogRef.close()
            })
          )
        ];
      }
    }
  });
};

const checkPayloadSubmitQuote = async (packages: any[], isMultipleOptionForSamePassenger?: boolean) => {
  const agent = cloneDeep(isWorksheetRequote.value && !isClearAgencyInfo.value ? getUserQuote(quote.value) : getUserParam());
  const payloadData = {
    ...agent,
    lang: props.selectedLanguage,
    gContact: props.advancedForm[0].groupContact,
    gName: props.advancedForm[0].groupName,
    gTypeFr: props.advancedForm[0].groupTypeFr,
    gType: props.advancedForm[0].groupType,
    qType: PACKAGE,
    gTypeId: props.advancedForm[0].groupTypeId,
    packages: packages,
    advanced: true,
    insta: props.advancedForm[0].instaQuote,
    avgPaxRoom: props.avgPaxRoomData,
    originalQuote: renderOriginQuote(true, quote.value?.sk),
    isMultipleOptionForSamePassenger: isMultipleOptionForSamePassenger,
    isReplicate: isWorksheetRequote.value
  };

  const checkOverDatePackage = packages?.some((item) => validateDateTime(item?.depDate, props.advancedForm[0].groupType));
  if (checkOverDatePackage) {
    showModalErrorPackage();
  } else {
    const isMaxRoomValid = await checkMaxRooms(payloadData);
    if (!isMaxRoomValid) {
      showValidateQuoteFormNotification(t('advance_quote.form.max_rooms_invalid'));
    } else if (checkHotelInBlackoutDate(payloadData.packages)) {
      showValidateQuoteFormNotification(t('advance_quote.form.has_black_out_period'));
    } else {
      submitQuote(payloadData, isSocketConnected.value);
    }
  }
};

const packageDataSubmit = () => {
  const packagesTemp: any = [];
  packageData.forEach((m, index) => {
    packagesTemp.push(renderPackageTemp(m, durationDate.value, index, dateValue.value));
  });
  return packagesTemp;
};

const handleSubmitOption = (event: any) => {
  const packageData = packageDataSubmit();
  const total = checkTotalAdultAndRoom(packageData);
  const isRequote = isWorksheetRequote.value;
  const validateRequoteWorksheetSubmit = (isValidateRequote: boolean) => {
    if (isValidateRequote && (checkAdultEachPackages(packageData, packageMinimumConfig.value?.minPax) || checkRoomsEachPackage(packageData, packageMinimumConfig.value?.minRoomsCabins))) {
      showValidateQuoteFormNotification();
    } else {
      checkPayloadSubmitQuote(packageData, true);
    }
  };

  const checkOptionNotMultipleForSamePassenger = () => {
    if (!isRequote && (checkTotalPassenger(packageData, packageMinimumConfig.value?.minPax) || checkTotalNumberOfRooms(packageData, packageMinimumConfig.value?.minRoomsCabins))) {
      showValidateQuoteFormNotification();
    } else if (!isRequote) {
      const totalPassenger = total[0].adtNo + total[0].chdNo;
      showConfirmQuoteCriteria(packageData, totalPassenger, total[0].numOfRooms, false);
    } else {
      checkPayloadSubmitQuote(packageData, false);
    }
  };
  if (event) {
    validateRequoteWorksheetSubmit(!isRequote);
  } else {
    checkOptionNotMultipleForSamePassenger();
  }
};

const handleSubmitPackage = async () => {
  await emits('submit');
  isShowError.value = true;
  cleanEmptyRooms(packageData);
  v$.value.$validate();
  if (!v$.value.$error && props.isValidation) {
    if (packageData.length > 1) {
      showValidateOptionModal();
    }
    if ((checkTotalPassenger(packageDataSubmit(), packageMinimumConfig.value?.minPax) || checkTotalNumberOfRooms(packageDataSubmit(), packageMinimumConfig.value?.minRoomsCabins)) && !isWorksheetRequote.value && packageData.length <= 1) {
      showValidateQuoteFormNotification();
      return;
    }
    if (packageData.length <= 1) {
      checkPayloadSubmitQuote(packageDataSubmit());
    }
  }
};

const submitQuote = (payload: any, hasSocket: boolean) => {
  if (hasSocket) {
    showWaitingModalPackage();
  }
  submitQuoteStore
    .handleSubmit(payload, hasSocket, openModalTimeOut, openModalErrorPricing)
    .then(() => {
      if (!hasSocket) {
        onAfterSubmitQuote({ pdfUrl: dataSubmit.value.pdf });
      }
    })
    .catch((e) => {
      if (e.message !== 'Timeout error') {
        toast.add({
          severity: 'error',
          summary: '',
          detail: t('notification.save_error'),
          life: 5000
        });
      }
    });
};

const resetHotelPackageData = (h) => {
  h.hotel = '';
};

const resetRoomPackageData = (r) => {
  r.room = '';
};

const removePackage = () => {
  packageData.forEach((p) => {
    resetPackageData(p);
    p.hotels.forEach((h) => {
      resetHotelPackageData(h);
      if (p.hotels.length > 1) {
        p.hotels.splice(1);
      }
      h.rooms.forEach((r) => {
        resetRoomPackageData(r);
        if (h.rooms.length > 1) {
          h.rooms.splice(1);
        }
      });
    });
  });
  if (packageData.length > 1) {
    packageData.splice(1);
  }
};

const handleShowConfirmModalPackage = (value, quoteId) => {
  if (value) {
    dialog.open(PDFModal, {
      props: {
        header: t('create_quote.pdf_view'),
        data: value,
        modal: true,
        style: {
          width: '60vw'
        },
        breakpoints: {
          '960px': '75vw',
          '640px': '90vw'
        },
        maximizable: true
      }
    });
  } else {
    dialog.open(SubmitNotificationModal, {
      props: {
        header: t('advance_quote.confirm_quote_id'),
        data: quoteId,
        modal: true
      }
    });
  }
};

const updateDateValue = (e, index, packageValue) => {
  if (dateValue.value.length === 0) {
    dateValue.value.push(new Date(DateTime.fromJSDate(e).plus({ days: packageValue[index].duration.name || 0 })));
  } else {
    const findIndex = Object.keys(dateValue.value).indexOf(index.toString());
    if (findIndex === -1) {
      dateValue.value.push(new Date(DateTime.fromJSDate(e).plus({ days: packageValue[index].duration.name || 0 })));
    } else {
      dateValue.value[findIndex] = new Date(DateTime.fromJSDate(e).plus({ days: packageValue[index].duration.name || 0 }));
    }
  }
};

const updateDurationDate = (e: any, index: number, packageValue: number) => {
  if (durationDate.value.length === 0) {
    durationDate.value.push(packageValue[index].dateRange ? new Date(DateTime.fromJSDate(packageValue[index].dateRange).plus({ days: e.value.name || 0 })) : '');
    return;
  }
  const findIndex = Object.keys(durationDate.value).indexOf(index.toString());
  if (findIndex === -1) {
    durationDate.value.push(packageValue[index].dateRange ? new Date(DateTime.fromJSDate(packageValue[index].dateRange).plus({ days: e.value.name || 0 })) : '');
  } else {
    durationDate.value[index] = packageValue[index].dateRange ? new Date(DateTime.fromJSDate(packageValue[index].dateRange).plus({ days: e.value.name || 0 })) : '';
  }
};

const openModalTimeOut = () => {
  clearAllDataPackage();
  const modalRef = dialog.open(ValidateQuoteFormNotification, {
    data: {
      title: t('notification.thank_for_your_patience'),
      message: t('notification.timeout_message_notice'),
      isAlignCenter: true
    },
    props: {
      header: t('advance_quote.form.notice'),
      modal: true
    },
    templates: {
      footer: () => {
        return [h('div', { class: 'text-center' }, h(Button, { autofocus: true, label: t('common.buttons.ok'), onClick: () => modalRef.close() }))];
      }
    }
  });
};

const openModalErrorPricing = () => {
  dialog.open(ValidateQuoteFormNotification, {
    data: {
      message: t('notification.pricing_error'),
      isAlignCenter: true
    },
    props: {
      header: t('common.error'),
      modal: true
    }
  });
};

const showWaitingModalPackage = () => {
  const dialogRef = dialog.open(WaitingSubmitModal, {
    props: {
      modal: true,
      showHeader: false,
      style: {
        width: '560px'
      },
      breakpoints: {
        '640px': '95vw'
      }
    },
    emits: {
      submitSuccess: (quoteData) => {
        dialogRef.close();
        onAfterSubmitQuote(quoteData);
      },
      closeModal: () => {
        dialogRef.close();
      }
    }
  });
};

const renderQuoteTabIntroHtml = () => {
  if (currentLanguage === LANG_FRENCH) {
    return h('div', { class: 'english-term', innerHTML: props.quoteIntro.termsFr });
  } else {
    return h('div', { class: 'french-term', innerHTML: props.quoteIntro.terms });
  }
};

watch(
  () => packageData,
  (newValue) => {
    newValue.forEach((packageVal) => {
      if (!packageVal.adults) {
        packageVal.adults = 0;
      }
      if (!packageVal.children) {
        packageVal.children = 0;
      }
      if (!packageVal.numOfRooms) {
        packageVal.numOfRooms = 0;
      }
    });
  },
  { deep: true }
);

onUnmounted(async () => {
  clearAllDataPackage();
});
</script>
<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="mb-3 quote-title" v-if="quoteIntro">
    <renderQuoteTabIntroHtml></renderQuoteTabIntroHtml>
  </div>
  <div class="package-form-option">
    <PackageFormOption
      :packageData="packageData"
      :advancedForm="advancedForm"
      :validate="v$.$each.$response.$errors"
      :isShowError="isShowError"
      :calendarData="calendarData"
      :durationDate="durationDate"
      :dateValue="dateValue"
      :listGatewaysByDest="gatewaysByDestination"
      @updateDurationDate="(event, index, data) => updateDurationDate(event, index, data)"
      @updateDateValue="(event, index, data) => updateDateValue(event, index, data)"
      @addOptionPackage="addOptionPackage()"
      @removeOptionPackage="removeOptionPackage()"
      @addHotelPackage="(indexOption) => addHotelPackage(indexOption)"
      @removeHotelPackage="(indexOption) => removeHotelPackage(indexOption)"
      @addRoomPackage="(indexOption, indexHotel) => addRoomPackage(indexOption, indexHotel)"
      @removeRoomPackage="(indexOption, indexHotel) => removeRoomPackage(indexOption, indexHotel)"
    />
  </div>
  <div class="flex gap-3">
    <Button id="submit-id" :label="$t('advance_quote.form.submit')" class="button-submit mt-2 bg-primary uppercase" @click="handleSubmitPackage()"></Button>
    <Button id="clear-id" :label="$t('common.buttons.clear')" class="button-submit mt-2 bg-primary uppercase" @click="clearAllDataPackage()"></Button>
  </div>
</template>
